import type { UnderwritingQuestionsForm } from "@covertree/new-business-forms";
import type {
  CreateOrUpdateEndorsementMutation,
  EndorsementEligibilityBlockReason,
  EndorsementExtraCoveragesFragment,
  EndorsementFailureReason,
  EndorsementStatus,
  EndorsementUnderwritingDecision,
  EndorsementUnderwritingFragment,
  PolicyLevelCoveragesFragmentFragment,
  UnitLevelBaseCoveragesFragmentFragment,
} from "@covertree/agent-portal-graphql";

import type {
  BasicEndorsementDetailsState,
  InsuredTabAdditionalInterest,
  InsuredTabFormValues,
} from "../basicEndorsementDetails/types";

/**
 * `units[].unitLevelCoverages` as returned in `projectedPolicy`
 */
export type ProjectedUnitLevelCoverages =
  UnitLevelBaseCoveragesFragmentFragment & EndorsementExtraCoveragesFragment;

export type ProjectedPolicyLevelCoverages =
  PolicyLevelCoveragesFragmentFragment["policyLevelCoverages"];

/** Policy- and unit-level UW questions, as returned in `projectedPolicy`. */
export type ProjectedUWQuestions =
  EndorsementUnderwritingFragment["policyLevelUnderwritingQuestions"];

/** UW answers of the draft: policy-level and the first unit's. */
export interface ProjectedUWAnswers {
  policyLevel: EndorsementUnderwritingFragment["policyLevelUnderwritingAnswers"];
  unitLevel?: EndorsementUnderwritingFragment["units"][number]["unitLevelUnderwritingAnswers"];
}

export type EndorsementMode = "create" | "update" | "issue" | "review";

/**
 * The `errorType` values the issue mutation rejects with, extended with
 * `GlobalError` for the unexpected failures the backend never names.
 */
export const IssueEndorsementStatus = {
  GlobalError: "GlobalError",
  PaymentError: "PaymentError",
  DraftRenewalError: "DraftRenewalError",
  ActiveRenewalError: "ActiveRenewalError",
  PolicyExpiredError: "PolicyExpiredError",
  PolicyCancelledError: "PolicyCancelledError",
  NonRenewalIssuedError: "NonRenewalIssuedError",
  UnderwritingDeclinedError: "UnderwritingDeclinedError",
  UnderwritingReferredError: "UnderwritingReferredError",
  ConflictingEndorsementError: "ConflictingEndorsementError",
} as const;

export type IssueEndorsementStatus =
  (typeof IssueEndorsementStatus)[keyof typeof IssueEndorsementStatus];

export type PaymentMethodKey = "card" | "ach" | "mortgageBill";

export type PaymentStatus = "idle" | "loading" | "success" | "error";

export interface PaymentState {
  status: PaymentStatus;
  error?: Error;
}

/**
 * The loaded endorsement draft, as the slice holds it.
 *
 * Assembled from `getEndorsement` (and refreshed by `createOrUpdateEndorsement`)
 * rather than mirroring either operation's shape one-to-one: it provides the
 * default values for the later-step forms (Coverage / Summary) plus the Insured
 * step's `insuredTabOverlay`, which is derived, not returned as-is.
 */
export interface EndorsementData {
  endorsementId: string | null;
  premiumDelta?: number | null;
  /** Refreshed price change from the last createOrUpdate mutation, if any. */
  priceChange?: CreateOrUpdateEndorsementMutation["createOrUpdateEndorsement"]["priceChange"];
  /**
   * Coverages of the first unit, from the mutation's `projectedPolicy`. Source
   * of the Coverage step's Extra Coverages list.
   */
  unitLevelCoverages?: ProjectedUnitLevelCoverages;
  /**
   * Policy-wide coverages from the mutation's `projectedPolicy`. Source of the
   * Coverage step's policy-level extra coverages.
   */
  policyLevelCoverages?: ProjectedPolicyLevelCoverages;
  /**
   * UW questions the rules return for the draft (policy-level first, then the
   * units'). Source of the Coverage step's Underwriting section.
   */
  underwritingQuestions?: ProjectedUWQuestions;
  /** UW answers stored on the draft (pre-filled from the in-force policy). */
  underwritingAnswers?: ProjectedUWAnswers;
  /**
   * A loaded draft's saved Insured Info edits, resolved from the draft's
   * `projectedPolicy`. The Insured step's form overlays this on top of the
   * policy baseline to build its defaults; `undefined` for a new draft.
   */
  insuredTabOverlay?: Partial<InsuredTabFormValues>;
  status?: EndorsementStatus;
  failureReason?: EndorsementFailureReason;
  underwritingDecision?: EndorsementUnderwritingDecision;
  underwritingNotes?: string[];
  /**
   * What the backend actually persisted for the Insured step, from the last
   * save/reprice's `projectedPolicy` — only fields an endorsement can change.
   *
   * Deliberately **not** written back into the form. Doing that overwrote values
   * the agent had typed: a stale projected `modelYear` replaced theirs, and the
   * next step's save posted the stale one, reverting the draft in Socotra. While
   * the agent is editing, the form is the source of truth.
   *
   * Kept so a save can be *reconciled* against what was submitted — reporting a
   * field the backend did not persist, rather than silently overwriting the form
   * with the difference.
   */
  insuredTabProjection?: Partial<InsuredTabFormValues>;
  /** Interests after the last save, carrying the ids the backend assigned. */
  additionalInterests?: InsuredTabAdditionalInterest[] | null;
  [key: string]: unknown;
}

/**
 * Form payload submitted to create/update the endorsement.
 *
 * Extends the flat Insured Info shape with the `units` array the Coverage step
 * writes extra coverage values into. Those values are an open record because the
 * keys are driven by the backend's coverage list; `buildCoveragesInput` narrows
 * them to the coverages the endorsement may actually change.
 */
export interface EndorsementFormPayload
  extends InsuredTabFormValues,
    UnderwritingQuestionsForm {
  units?: Array<{ unitLevelCoverages?: Record<string, unknown> }>;
}

/** Argument of the `createOrUpdateEndorsement` thunk. */
export interface CreateOrUpdateEndorsementArgs {
  values: EndorsementFormPayload;
  /**
   * Unit the lot/unit + construction edits belong to, read from the policy.
   * Omit to send no `unitChanges` at all.
   */
  unitId?: string;
  /** Form defaults, i.e. the last saved state; fields equal to it are left out. */
  savedValues?: Partial<EndorsementFormPayload>;
}

export interface EndorsementLoadingState {
  show: boolean;
  messages: string[];
  maxLoadTime?: number;
  messageRotationInterval?: number;
}

export interface ShowLoadingFramePayload {
  messages: string[];
  maxLoadTime?: number;
  messageRotationInterval?: number;
}

export type EndorsementLoadStatus = "idle" | "pending" | "succeeded" | "failed";

export interface EndorsementState {
  /** Loading-frame overlay state. */
  loading: EndorsementLoadingState;
  /** Whether any query/mutation is in progress. */
  loadingFromBackend: boolean;
  mode: EndorsementMode;
  policyLocator: string | null;
  endorsementId: string | null;
  endorsement?: EndorsementData;
  endorsementLoadStatus: EndorsementLoadStatus;
  premiumDelta: number | null;
  deltaTax: number | null;
  deltaFees: number | null;
  lastConfirmedDelta: number | null;
  /**
   * Whether the last save/reprice came back without a price change, meaning the
   * backend could not price the draft. The delta fields then hold the last
   * figure that *was* priced, so this is what stops the footer presenting a
   * stale number as current.
   */
  isPriceUnavailable: boolean;
  isRecalculating: boolean;
  hasUnsavedChanges: boolean;
  payment: PaymentState;
  endorsementIssueBlockReason?: IssueEndorsementStatus;
  isUnderwritingChecked: boolean;
  isCheckingUnderwriting: boolean;
  /**
   * Earliest allowed endorsement effective date (ISO), sourced from a separate
   * query and used as the lower bound for the effective-date picker.
   */
  minDateEffectiveDate?: string;
  /**
   * Latest allowed endorsement effective date (ISO), sourced from the eligibility
   * query and used as the upper bound for the effective-date picker.
   */
  maxDateEffectiveDate?: string;
  /** When the endorsement is not eligible, this stores the blocking reason enum. */
  eligibilityBlockReason?: EndorsementEligibilityBlockReason;
  error?: Error;
}

/** Root shape this slice sees through `thunkAPI.getState()` (combined under `endorsement`). */
export type RootWithEndorsement = {
  endorsement: {
    endorsement: EndorsementState;
    basicEndorsementDetails: BasicEndorsementDetailsState;
  };
};

export interface UnderwritingFailure {
  decision: EndorsementUnderwritingDecision;
  status: EndorsementStatus;
  notes: string[];
}
